import type { GameState, Card, Player, Artist } from '../types/game'
import { createDeck, shuffleDeck } from './deck'
import { createInitialBoard, rankArtists, updateBoardWithRoundResults } from './valuation'
import { ARTISTS, CARD_DISTRIBUTION, STARTING_MONEY, getTurnOrder } from './constants'
import { SeededRandom } from './seededRandom'

let cardIdCounter = 1000 // Start from 1000 to avoid conflicts with real deck cards

function generateTestCardId(): string {
  return `test_card_${cardIdCounter++}`
}

/**
 * Simulated auction result for test scenario generation
 */
interface SimulatedAuction {
  card: Card
  sold: boolean
  winnerIndex: number | null
  salePrice: number | null
}

/**
 * Simulate an auction with realistic bidding behavior using seeded RNG
 */
function simulateAuction(
  card: Card,
  bidders: number[],
  playerMoney: Record<number, number>,
  rng: SeededRandom
): SimulatedAuction {
  const auctionType = card.auctionType

  // For fixed price, always sells at random price between 10-30
  if (auctionType === 'fixed_price') {
    const price = rng.nextInt(1, 3) * 10 // 10, 20, or 30
    const eligibleBidders = bidders.filter(i => playerMoney[i] >= price)
    if (eligibleBidders.length === 0) {
      return { card, sold: false, winnerIndex: null, salePrice: null }
    }
    const winner = rng.nextItem(eligibleBidders)
    return { card, sold: true, winnerIndex: winner, salePrice: price }
  }

  // For one offer, single sealed bid
  if (auctionType === 'one_offer') {
    const eligibleBidders = bidders.filter(i => playerMoney[i] >= 10)
    if (eligibleBidders.length === 0) {
      return { card, sold: false, winnerIndex: null, salePrice: null }
    }
    const bidder = rng.nextItem(eligibleBidders)
    const maxBid = Math.min(playerMoney[bidder], 50)
    const bid = rng.nextInt(1, Math.floor(maxBid / 10)) * 10
    return { card, sold: true, winnerIndex: bidder, salePrice: bid }
  }

  // For hidden auction, pick highest of random sealed bids
  if (auctionType === 'hidden') {
    const bids = bidders
      .filter(i => playerMoney[i] >= 10)
      .map(i => {
        const maxBid = Math.min(playerMoney[i], 60)
        return {
          playerIndex: i,
          bid: rng.nextInt(1, Math.floor(maxBid / 10)) * 10
        }
      })
      .filter(b => b.bid > 0)

    if (bids.length === 0) {
      return { card, sold: false, winnerIndex: null, salePrice: null }
    }

    bids.sort((a, b) => b.bid - a.bid)
    const winner = bids[0]
    return { card, sold: true, winnerIndex: winner.playerIndex, salePrice: winner.bid }
  }

  // For open auction, simulate competitive bidding
  if (auctionType === 'open' || auctionType === 'double') {
    const bids = bidders
      .filter(i => playerMoney[i] >= 10)
      .map(i => {
        const maxBid = Math.min(playerMoney[i], auctionType === 'double' ? 70 : 60)
        return {
          playerIndex: i,
          maxBid: rng.nextInt(1, Math.floor(maxBid / 10)) * 10
        }
      })

    if (bids.length === 0) {
      return { card, sold: false, winnerIndex: null, salePrice: null }
    }

    bids.sort((a, b) => b.maxBid - a.maxBid)

    if (bids.length === 1) {
      return { card, sold: true, winnerIndex: bids[0].playerIndex, salePrice: 10 }
    }

    const winner = bids[0]
    const runnerUp = bids[1]
    const finalPrice = Math.min(
      runnerUp.maxBid + 10,
      winner.maxBid
    )

    return { card, sold: true, winnerIndex: winner.playerIndex, salePrice: finalPrice }
  }

  return { card, sold: false, winnerIndex: null, salePrice: null }
}

/**
 * Create a test card with specific properties
 */
function createTestCard(
  artist: string,
  auctionType: string,
  artworkId: string
): Card {
  return {
    id: generateTestCardId(),
    artist: artist as any,
    auctionType: auctionType as any,
    artworkId
  }
}

/**
 * Create a round-end test scenario for debugging round transitions
 *
 * This creates a realistic game state where:
 * - One random artist has 4 cards played (one away from ending the round)
 * - 6-8 auctions have been simulated with realistic bidding
 * - Players have purchases with appropriate prices and money deducted
 * - Human player has the 5th card of that artist in hand (trigger card)
 * - It's the human player's turn to play
 *
 * @param playerCount - Number of players (3, 4, or 5)
 * @param humanPlayerIndex - Index of human player (default 0)
 * @param seed - Random seed for deterministic scenarios (default Date.now())
 */
export function createRoundEndTestScenario(
  playerCount: 3 | 4 | 5,
  humanPlayerIndex: number = 0,
  seed: number = Date.now()
): GameState {
  const rng = new SeededRandom(seed)

  // Create a fresh deck for remaining cards
  const fullDeck = createDeck()
  const usedCardIds = new Set<string>()

  // Initialize players with starting money
  const players: Player[] = Array.from({ length: playerCount }, (_, i) => ({
    id: i === humanPlayerIndex ? 'human_player' : `ai_player_${i}`,
    name: i === humanPlayerIndex ? 'You' : `AI ${i + 1}`,
    money: STARTING_MONEY,
    hand: [],
    purchasedThisRound: [],
    purchases: [],
    isAI: i !== humanPlayerIndex,
    aiDifficulty: i !== humanPlayerIndex ? 'medium' : undefined
  }))

  // Pick a random artist to be close to ending the round (4 cards played)
  const nearEndArtistIndex = rng.nextInt(0, ARTISTS.length - 1)
  const nearEndArtist = ARTISTS[nearEndArtistIndex]

  // Track player money as auctions happen
  const playerMoney: Record<number, number> = {}
  players.forEach((_, i) => playerMoney[i] = STARTING_MONEY)

  // Simulate auctions with 6-8 total (increased from 3-6)
  const playedCards: Card[] = []
  const discardPile: Card[] = []
  const board = createInitialBoard()
  const cardsPlayedPerArtist: Record<string, number> = {}
  ARTISTS.forEach(a => cardsPlayedPerArtist[a] = 0)

  // Simulate 6-8 random auctions before the test scenario
  const auctionCount = 6 + rng.nextInt(0, 2) // 6, 7, or 8
  const playerIndexes = Array.from({ length: playerCount }, (_, i) => i)

  for (let i = 0; i < auctionCount; i++) {
    const auctioneerIndex = i % playerCount
    const turnOrder = getTurnOrder(auctioneerIndex, playerCount)

    // Pick a random card (not the near-end artist if we already have 4)
    let artistForCard = rng.nextItem(ARTISTS)
    const currentNearEndCount = cardsPlayedPerArtist[nearEndArtist]

    // Don't play more than 4 of the near-end artist
    if (artistForCard === nearEndArtist && currentNearEndCount >= 4) {
      const otherArtists = ARTISTS.filter(a => a !== nearEndArtist && cardsPlayedPerArtist[a] < 4)
      artistForCard = otherArtists.length > 0 ? rng.nextItem(otherArtists) : nearEndArtist
    }

    // Pick auction type
    const auctionTypes = ['open', 'one_offer', 'hidden', 'fixed_price']
    const auctionType = rng.nextItem(auctionTypes)

    const card = createTestCard(artistForCard, auctionType, `test_${artistForCard}_${i}`)

    // Simulate the auction
    const result = simulateAuction(card, turnOrder, playerMoney, rng)

    if (result.sold && result.winnerIndex !== null && result.salePrice !== null) {
      const soldCard: Card = {
        ...result.card,
        purchasePrice: result.salePrice,
        purchasedRound: 1
      }

      players[result.winnerIndex].purchasedThisRound.push(soldCard)
      playerMoney[result.winnerIndex] -= result.salePrice
      discardPile.push(soldCard)
    } else {
      const auctioneerCard: Card = {
        ...result.card,
        purchasePrice: 0,
        purchasedRound: 1
      }

      players[auctioneerIndex].purchasedThisRound.push(auctioneerCard)
      discardPile.push(auctioneerCard)
    }

    playedCards.push(result.card)
    cardsPlayedPerArtist[result.card.artist] = (cardsPlayedPerArtist[result.card.artist] || 0) + 1
    usedCardIds.add(result.card.id)
  }

  // Now ensure near-end artist has exactly 4 cards played
  while (cardsPlayedPerArtist[nearEndArtist] < 4) {
    const auctionTypes = ['open', 'one_offer', 'hidden', 'fixed_price']
    const auctionType = rng.nextItem(auctionTypes)
    const card = createTestCard(nearEndArtist, auctionType, `test_${nearEndArtist}_${playedCards.length}`)

    const auctioneerIndex = playedCards.length % playerCount
    const turnOrder = getTurnOrder(auctioneerIndex, playerCount)
    const result = simulateAuction(card, turnOrder, playerMoney, rng)

    if (result.sold && result.winnerIndex !== null && result.salePrice !== null) {
      const soldCard: Card = {
        ...result.card,
        purchasePrice: result.salePrice,
        purchasedRound: 1
      }
      players[result.winnerIndex].purchasedThisRound.push(soldCard)
      playerMoney[result.winnerIndex] -= result.salePrice
      discardPile.push(soldCard)
    } else {
      const auctioneerCard: Card = {
        ...result.card,
        purchasePrice: 0,
        purchasedRound: 1
      }
      players[auctioneerIndex].purchasedThisRound.push(auctioneerCard)
      discardPile.push(auctioneerCard)
    }

    playedCards.push(result.card)
    cardsPlayedPerArtist[nearEndArtist]++
    usedCardIds.add(result.card.id)
  }

  // Update player money to reflect auction results
  players.forEach((player, i) => {
    player.money = playerMoney[i]
  })

  // Create the 5th card for human player (the trigger card)
  const triggerCard = createTestCard(
    nearEndArtist,
    'open',
    `trigger_${nearEndArtist}`
  )

  // Add to human player's hand
  players[humanPlayerIndex].hand.push(triggerCard)
  usedCardIds.add(triggerCard.id)

  // Give other players cards from the deck
  const remainingDeck = shuffleDeck(
    fullDeck.filter(c => !usedCardIds.has(c.id))
  )

  // Deal cards to all players
  const cardsPerPlayer = playerCount === 3 ? 6 : playerCount === 4 ? 4 : 3
  players.forEach((player, i) => {
    if (i !== humanPlayerIndex) {
      const cardsNeeded = cardsPerPlayer
      player.hand.push(...remainingDeck.splice(0, cardsNeeded))
    } else {
      // Human player gets a few more cards
      const extraCards = cardsPerPlayer - 1 // Already has trigger card
      player.hand.push(...remainingDeck.splice(0, extraCards))
    }
  })

  // Update board with played cards
  playedCards.forEach(card => {
    board.playedCards[card.artist].push(card)
  })

  // Create the game state
  const gameState: GameState = {
    players,
    deck: remainingDeck,
    discardPile,
    board,
    round: {
      roundNumber: 1,
      cardsPlayedPerArtist,
      currentAuctioneerIndex: humanPlayerIndex,
      phase: {
        type: 'awaiting_card_play',
        activePlayerIndex: humanPlayerIndex
      }
    },
    gamePhase: 'playing',
    winner: null,
    eventLog: []
  }

  console.log('[createRoundEndTestScenario] Created test scenario:', {
    seed,
    playerCount,
    humanPlayerIndex,
    nearEndArtist,
    auctionCount,
    cardsPlayedPerArtist,
    humanHand: players[humanPlayerIndex].hand.map(c => ({
      id: c.id,
      artist: c.artist,
      auctionType: c.auctionType
    })),
    playerMoney: players.map(p => ({ name: p.name, money: p.money })),
    purchasedThisRound: players.map(p => ({ name: p.name, count: p.purchasedThisRound.length }))
  })

  return gameState
}

/**
 * Create a round 4 end game test scenario for testing game end logic
 *
 * This creates a realistic game state where:
 * - 3 complete rounds have been played (rounds 1, 2, 3)
 * - 6-8 auctions simulated per round
 * - Artist values are populated for all 3 rounds
 * - Players have accumulated paintings across all rounds
 * - Round 4 is set with human player having trigger card (artist at 4/5 played)
 * - Human player's turn to play the trigger card
 *
 * @param playerCount - Number of players (3, 4, or 5)
 * @param humanPlayerIndex - Index of human player (default 0)
 * @param seed - Random seed for deterministic scenarios (default Date.now())
 */
export function createRound4EndTestScenario(
  playerCount: 3 | 4 | 5,
  humanPlayerIndex: number = 0,
  seed: number = Date.now()
): GameState {
  const rng = new SeededRandom(seed)

  // Create a fresh deck for remaining cards
  const fullDeck = createDeck()
  const usedCardIds = new Set<string>()

  // Initialize players with starting money
  const players: Player[] = Array.from({ length: playerCount }, (_, i) => ({
    id: i === humanPlayerIndex ? 'human_player' : `ai_player_${i}`,
    name: i === humanPlayerIndex ? 'You' : `AI ${i + 1}`,
    money: STARTING_MONEY,
    hand: [],
    purchasedThisRound: [],
    purchases: [],
    isAI: i !== humanPlayerIndex,
    aiDifficulty: i !== humanPlayerIndex ? 'medium' : undefined
  }))

  // Track player money across all rounds
  const playerMoney: Record<number, number> = {}
  players.forEach((_, i) => playerMoney[i] = STARTING_MONEY)

  // Initialize board and tracking
  const board = createInitialBoard()
  const allPlayedCards: Card[] = []
  const allDiscardPile: Card[] = []

  // Simulate 3 complete rounds
  for (let roundNumber = 1; roundNumber <= 3; roundNumber++) {
    console.log(`[createRound4EndTestScenario] Simulating round ${roundNumber}`)

    // Reset round-specific tracking
    const cardsPlayedPerArtist: Record<string, number> = {}
    ARTISTS.forEach(a => cardsPlayedPerArtist[a] = 0)

    // Clear purchasedThisRound for new round
    players.forEach(p => {
      // Move previous round's purchases to permanent collection
      if (p.purchasedThisRound.length > 0) {
        p.purchases = [...(p.purchases || []), ...p.purchasedThisRound]
      }
      p.purchasedThisRound = []
    })

    // Simulate 6-8 auctions this round
    const auctionCount = 6 + rng.nextInt(0, 2)
    const playerIndexes = Array.from({ length: playerCount }, (_, i) => i)

    for (let i = 0; i < auctionCount; i++) {
      const auctioneerIndex = i % playerCount
      const turnOrder = getTurnOrder(auctioneerIndex, playerCount)

      // Pick random artist
      let artistForCard = rng.nextItem(ARTISTS)

      // Distribute auctions across artists (not too concentrated)
      const maxCardsPerArtist = 4
      if (cardsPlayedPerArtist[artistForCard] >= maxCardsPerArtist) {
        const otherArtists = ARTISTS.filter(a => cardsPlayedPerArtist[a] < maxCardsPerArtist)
        artistForCard = otherArtists.length > 0 ? rng.nextItem(otherArtists) : artistForCard
      }

      // Pick auction type
      const auctionTypes = ['open', 'one_offer', 'hidden', 'fixed_price', 'double']
      const auctionType = rng.nextItem(auctionTypes)

      const card = createTestCard(artistForCard, auctionType, `round${roundNumber}_${artistForCard}_${i}`)

      // Simulate auction
      const result = simulateAuction(card, turnOrder, playerMoney, rng)

      if (result.sold && result.winnerIndex !== null && result.salePrice !== null) {
        const soldCard: Card = {
          ...result.card,
          purchasePrice: result.salePrice,
          purchasedRound: roundNumber
        }
        players[result.winnerIndex].purchasedThisRound.push(soldCard)
        playerMoney[result.winnerIndex] -= result.salePrice
        allDiscardPile.push(soldCard)
      } else {
        const auctioneerCard: Card = {
          ...result.card,
          purchasePrice: 0,
          purchasedRound: roundNumber
        }
        players[auctioneerIndex].purchasedThisRound.push(auctioneerCard)
        allDiscardPile.push(auctioneerCard)
      }

      allPlayedCards.push(result.card)
      cardsPlayedPerArtist[result.card.artist]++
      usedCardIds.add(result.card.id)
    }

    // Calculate round results and update board
    const roundResults = rankArtists(cardsPlayedPerArtist)
    const updatedBoard = updateBoardWithRoundResults(board, roundNumber - 1, roundResults)

    // Update board reference
    Object.assign(board.artistValues, updatedBoard.artistValues)

    console.log(`[createRound4EndTestScenario] Round ${roundNumber} results:`, {
      cardsPlayedPerArtist,
      rankings: roundResults.map(r => `${r.artist}: ${r.rank} (${r.value})`).join(', ')
    })
  }

  // NOW simulate Round 4 with auctions (just like other rounds)
  const roundNumber = 4
  console.log(`[createRound4EndTestScenario] Simulating round ${roundNumber}`)

  // Reset round-specific tracking
  const cardsPlayedPerArtist: Record<string, number> = {}
  ARTISTS.forEach(a => cardsPlayedPerArtist[a] = 0)

  // Clear purchasedThisRound for round 4 (move round 3 to permanent first)
  players.forEach(p => {
    if (p.purchasedThisRound.length > 0) {
      p.purchases = [...(p.purchases || []), ...p.purchasedThisRound]
    }
    p.purchasedThisRound = []
  })

  // Pick the artist that will be near ending (4 cards played)
  const nearEndArtistIndex = rng.nextInt(0, ARTISTS.length - 1)
  const nearEndArtist = ARTISTS[nearEndArtistIndex]

  // Simulate 6-8 auctions in Round 4
  const auctionCount = 6 + rng.nextInt(0, 2)
  const playerIndexes = Array.from({ length: playerCount }, (_, i) => i)

  for (let i = 0; i < auctionCount; i++) {
    const auctioneerIndex = i % playerCount
    const turnOrder = getTurnOrder(auctioneerIndex, playerCount)

    // Pick random artist (but track near-end artist carefully)
    let artistForCard = rng.nextItem(ARTISTS)

    // Don't exceed 4 cards for the near-end artist (we need it at exactly 4)
    if (artistForCard === nearEndArtist && cardsPlayedPerArtist[nearEndArtist] >= 4) {
      const otherArtists = ARTISTS.filter(a => a !== nearEndArtist && cardsPlayedPerArtist[a] < 4)
      artistForCard = otherArtists.length > 0 ? rng.nextItem(otherArtists) : nearEndArtist
    }

    // Pick auction type
    const auctionTypes = ['open', 'one_offer', 'hidden', 'fixed_price', 'double']
    const auctionType = rng.nextItem(auctionTypes)

    const card = createTestCard(artistForCard, auctionType, `round4_${artistForCard}_${i}`)

    // Simulate auction
    const result = simulateAuction(card, turnOrder, playerMoney, rng)

    if (result.sold && result.winnerIndex !== null && result.salePrice !== null) {
      const soldCard: Card = {
        ...result.card,
        purchasePrice: result.salePrice,
        purchasedRound: roundNumber
      }
      players[result.winnerIndex].purchasedThisRound.push(soldCard)
      playerMoney[result.winnerIndex] -= result.salePrice
      allDiscardPile.push(soldCard)
    } else {
      const auctioneerCard: Card = {
        ...result.card,
        purchasePrice: 0,
        purchasedRound: roundNumber
      }
      players[auctioneerIndex].purchasedThisRound.push(auctioneerCard)
      allDiscardPile.push(auctioneerCard)
    }

    allPlayedCards.push(result.card)
    cardsPlayedPerArtist[result.card.artist]++
    usedCardIds.add(result.card.id)
  }

  // Ensure near-end artist has exactly 4 cards played
  while (cardsPlayedPerArtist[nearEndArtist] < 4) {
    const auctionTypes = ['open', 'one_offer', 'hidden', 'fixed_price', 'double']
    const auctionType = rng.nextItem(auctionTypes)
    const card = createTestCard(nearEndArtist, auctionType, `round4_${nearEndArtist}_${allPlayedCards.length}`)

    const auctioneerIndex = allPlayedCards.length % playerCount
    const turnOrder = getTurnOrder(auctioneerIndex, playerCount)
    const result = simulateAuction(card, turnOrder, playerMoney, rng)

    if (result.sold && result.winnerIndex !== null && result.salePrice !== null) {
      const soldCard: Card = {
        ...result.card,
        purchasePrice: result.salePrice,
        purchasedRound: roundNumber
      }
      players[result.winnerIndex].purchasedThisRound.push(soldCard)
      playerMoney[result.winnerIndex] -= result.salePrice
      allDiscardPile.push(soldCard)
    } else {
      const auctioneerCard: Card = {
        ...result.card,
        purchasePrice: 0,
        purchasedRound: roundNumber
      }
      players[auctioneerIndex].purchasedThisRound.push(auctioneerCard)
      allDiscardPile.push(auctioneerCard)
    }

    allPlayedCards.push(result.card)
    cardsPlayedPerArtist[nearEndArtist]++
    usedCardIds.add(result.card.id)
  }

  // Update player money to reflect Round 4 auction results
  players.forEach((player, i) => {
    player.money = playerMoney[i]
  })

  // Create the 5th card for human player (the trigger card)
  const triggerCard = createTestCard(
    nearEndArtist,
    'open',
    `round4_trigger_${nearEndArtist}`
  )

  // Add to human player's hand
  players[humanPlayerIndex].hand.push(triggerCard)
  usedCardIds.add(triggerCard.id)

  // Deal remaining cards from deck (0 cards for round 4 in actual game)
  const remainingDeck = shuffleDeck(
    fullDeck.filter(c => !usedCardIds.has(c.id))
  )

  // Give other players a few cards (even though round 4 has 0 dealt, this lets them play if needed)
  const cardsForOthers = playerCount === 3 ? 2 : playerCount === 4 ? 2 : 1
  players.forEach((player, i) => {
    if (i !== humanPlayerIndex) {
      player.hand.push(...remainingDeck.splice(0, cardsForOthers))
    }
  })

  // Round 4 artist values are still 0 (will be calculated when round ends)
  // board.artistValues[3] = [0, 0, 0, 0, 0] for all artists

  // Create the game state for round 4
  const round4CardsPlayedPerArtist: Record<Artist, number> = {
    'Solstice Vane': 0,
    'Mira Faux': 0,
    'Atlas Zero': 0,
    'Vesper Kint': 0,
    'Luxe Nebula': 0,
  }
  round4CardsPlayedPerArtist[nearEndArtist] = 4

  const gameState: GameState = {
    players,
    deck: remainingDeck,
    discardPile: allDiscardPile,
    board,
    round: {
      roundNumber: 4,
      cardsPlayedPerArtist: round4CardsPlayedPerArtist,
      currentAuctioneerIndex: humanPlayerIndex,
      phase: {
        type: 'awaiting_card_play',
        activePlayerIndex: humanPlayerIndex
      }
    },
    gamePhase: 'playing',
    winner: null,
    eventLog: []
  }

  console.log('[createRound4EndTestScenario] Created end game scenario:', {
    seed,
    playerCount,
    humanPlayerIndex,
    nearEndArtist,
    round4CardsPlayedPerArtist,
    humanHand: players[humanPlayerIndex].hand.map(c => ({
      id: c.id,
      artist: c.artist,
      auctionType: c.auctionType
    })),
    humanMoney: players[humanPlayerIndex].money,
    humanPaintings: players[humanPlayerIndex].purchases?.length || 0,
    humanPurchasedThisRound: players[humanPlayerIndex].purchasedThisRound.length,
    boardArtistValues: board.artistValues,
    totalPlayers: players.length,
    allPlayerNames: players.map(p => p.name),
    allPlayerMoney: players.map(p => `${p.name}: $${p.money}k`),
  })

  return gameState
}
